import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { bindFormModule } from '../../shared/formBinding.js';
import { formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// Marks a request as wanting a server-rendered partial rather than a whole page.
// The same header liveList.js sends, and the same protocol on the server side
// (utils/renderFragment.js) - this form's contact group is a fragment for the
// same reason a filtered list is: the markup is described in one place, and the
// server owns it.
const FRAGMENT_HEADER = 'X-Fragment';

// The refs an empty option means "none" for. Sending '' would fail the
// isMongoId check, so the key comes off entirely and the server keeps or clears
// the value as its own rules dictate.
const OPTIONAL_REFS = ['verlustgrund', 'verantwortlichBenutzer'];

// Optional free-text fields. An empty one is sent as '' rather than dropped:
// dropping it would mean "leave unchanged" on a PATCH, so clearing the remark in
// the form would never reach the record.
const NULLABLE_TEXT = ['verlustBemerkung'];

// The container the shift checkboxes render into. See readForm below.
const SCHICHT_GRUPPE = 'job-schichten-gruppe';

// One subdoc array, assembled from its checkbox group and its primär radio.
//
// This is what the two groups on this form need and the customer's Betriebsarten
// did not: the API expects rows of `{ <ref>: id, primaer: bool }`, so a list of
// ticked ids is not enough on its own. The radio names the primary one, and a
// radio whose box is not ticked is ignored rather than trusted - the server
// would reject that row anyway, and the disabled state can be worked around.
function readZuordnungen(form, name, refField, radioName) {
  const primaer = form.querySelector(`input[name="${radioName}"]:checked`)?.value || null;

  return Array.from(form.querySelectorAll(`input[name="${name}"]:checked`)).map((box) => ({
    [refField]: box.value,
    primaer: box.value === primaer
  }));
}

// Turns the form into the JSON body the API expects.
function readForm(form) {
  const formData = new FormData(form);
  const data = Object.fromEntries(formData.entries());

  // Read off the DOM for the same reason the two groups below are: this is a
  // repeated key, and Object.fromEntries keeps only its last value - so a job
  // open to both Temporär and Festanstellung would otherwise submit as
  // Festanstellung alone. getAll returns [] when nothing is ticked, which is
  // what the validator rejects with a message on this field.
  data.arbeitsform = formData.getAll('arbeitsform');

  // The shifts are five independent booleans rather than one array, so each box
  // posts its own field name and a ticked one arrives as the string 'on', which
  // fails the isBoolean check server-side. Read off the DOM rather than named
  // here, so the catalogue in utils/schichten.js stays the only place the set is
  // written down - same as the customer form.
  form.querySelectorAll(`#${SCHICHT_GRUPPE} input[type="checkbox"]`).forEach((box) => {
    data[box.name] = box.checked;
  });

  // Both groups are rebuilt from the DOM rather than read off FormData:
  // Object.fromEntries keeps only the last value of a repeated key, and the
  // primär flag lives on a separate control entirely.
  data.fachgebiete = readZuordnungen(form, 'fachgebiete', 'fachgebiet', 'fachgebietPrimaer');
  data.ansprechpersonen = readZuordnungen(
    form,
    'ansprechpersonen',
    'ansprechperson',
    'ansprechpersonPrimaer'
  );

  // The two radios are controls of this form, not fields of the resource. Left
  // in, they would arrive as unknown keys - harmless, since the controller
  // whitelists what it reads, but sending them would suggest the API takes them.
  delete data.fachgebietPrimaer;
  delete data.ansprechpersonPrimaer;

  OPTIONAL_REFS.forEach((field) => {
    if (!data[field]) delete data[field];
  });

  NULLABLE_TEXT.forEach((field) => {
    if (data[field] !== undefined) data[field] = data[field].trim();
  });

  return data;
}

// A primär radio only means something for a Fachgebiet or contact that is
// actually selected, so each is disabled until its own box is ticked - and
// unticking a box gives up the flag rather than leaving it on a row that is no
// longer part of the job.
//
// One handler per group, delegated to the container, so the rows the fragment
// delivers are live without rebinding anything.
function bindPrimaerKopplung(container, boxName) {
  if (!container) return;

  container.addEventListener('change', (event) => {
    const box = event.target;
    if (box.name !== boxName) return;

    // data-zuordnung marks the row wrapper, rather than matching on the layout
    // classes: those describe how the row looks and are free to change.
    const zeile = box.closest('[data-zuordnung]');
    const radio = zeile?.querySelector('input[type="radio"]');
    if (!radio) return;

    radio.disabled = !box.checked;
    if (!box.checked) radio.checked = false;
  });
}

// The dependent contact group. Its options belong to the customer chosen above,
// so changing that customer refetches them from the server rather than
// filtering a list held in JavaScript - there is no second copy of the rows to
// go stale, and the scope that decides which contacts may be seen stays on the
// server.
//
// A contact of the old customer cannot stay ticked, which is what makes this a
// replacement rather than a filter: the fragment comes back with nothing
// selected, and that is correct.
function initAbhaengigeAnsprechpersonen(form) {
  const kunde = form.querySelector('#kunde');
  const container = form.querySelector('#job-ansprechpersonen');
  if (!kunde || !container) return;

  kunde.addEventListener('change', async () => {
    const params = kunde.value ? `?kunde=${encodeURIComponent(kunde.value)}` : '';

    container.setAttribute('aria-busy', 'true');

    try {
      const response = await window.fetch(`/jobs/ansprechpersonen${params}`, {
        headers: { [FRAGMENT_HEADER]: '1' },
        credentials: 'same-origin'
      });

      // A dead session redirects to /login, which arrives here as HTML that is
      // not our partial. Follow it rather than pasting a login form into the
      // form.
      if (response.redirected && new URL(response.url).pathname !== '/jobs/ansprechpersonen') {
        window.location.href = response.url;
        return;
      }

      if (!response.ok) throw new Error(`Fragment request failed: ${response.status}`);

      container.innerHTML = await response.text();
    } catch {
      // The group is left as it was, which is stale but readable. A toast rather
      // than a navigation: the user is mid-form, and reloading would throw away
      // everything they have typed so far.
      notifyError('Die Ansprechpersonen konnten nicht geladen werden.');
    } finally {
      container.removeAttribute('aria-busy');
    }
  });
}

// The block that appears and disappears with the status above it. The server
// renders the right state for the record as loaded; this keeps it right as
// somebody edits.
function initConditionalBlocks(form) {
  const status = form.querySelector('#status');
  const verlust = form.querySelector('#verlust-block');

  if (status && verlust) {
    status.addEventListener('change', () => {
      verlust.hidden = status.value !== 'closed_lost';
      // Moving the picker away from Verloren clears the reason too, so a hidden
      // field cannot submit a value the server would then reject as
      // inconsistent with the status beside it.
      if (verlust.hidden) {
        const grund = form.querySelector('#verlustgrund');
        if (grund) grund.value = '';
      }
    });
  }

  bindPrimaerKopplung(form.querySelector('#job-fachgebiete'), 'fachgebiete');
  bindPrimaerKopplung(form.querySelector('#job-ansprechpersonen'), 'ansprechpersonen');
  initAbhaengigeAnsprechpersonen(form);
}

// Where a submit goes. Three cases, because a job reaches this form three
// ways: editing one, creating one, and converting a staged job ad into one
// (N09) - the last keeps the ad and the job linked, which only the ad's own
// endpoint does.
function ziel({ jobId, inseratId }) {
  if (jobId) return (data) => http.patch(`/jobs/${jobId}`, data);
  if (inseratId) return (data) => http.post(`/stelleninserate/${inseratId}/job`, data);
  return (data) => http.post('/jobs', data);
}

async function submit(form) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { jobId, inseratId } = form.dataset;
  const data = readForm(form);

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    const { data: body } = await ziel({ jobId, inseratId })(data);

    // Editing in the dialog stays where it was: the page behind it - the list or
    // the job's own page - is already the right place, so it closes and
    // refetches rather than navigating.
    if (jobId && isFormModalOpen()) {
      hideLoading();
      // Before formModalSaved, not after: the caller's onSaved may navigate -
      // a detail page reloads itself - and Notiflix then paints into a document
      // that is being torn down, which throws into the catch below and shows an
      // error for a save that worked.
      notifySuccess('Job wurde aktualisiert.');
      formModalSaved();
      return;
    }

    // Creating goes to the new job either way. It is the record somebody is
    // about to keep working on - the Fachgebiete, the contacts and eventually
    // the candidate proposals all live on its own page.
    const id = jobId || body.data.job.id;
    window.location.href = withFlash(`/jobs/${id}`, jobId ? 'job-aktualisiert' : 'job-erstellt');
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;

    notifyError(
      err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
    );
  }
}

export function initJobForm() {
  bindFormModule('job-form', submit, initConditionalBlocks);
}
